import FileDownload from 'js-file-download';
import { useLoading } from '../../../../hooks/useLoading';
import { useNotifications } from '../../../../hooks/useNotifications';

/**
 * Shared hook for all service-delivery check forms.
 *
 * Handles:
 *   - calling the API with the FormData built by the check form
 *   - downloading the returned file (xlsx or zip)
 *   - surfacing success / error notifications
 *   - exposing isLoading state to disable the submit button
 *
 * Usage:
 *   const { handleSubmit, isLoading } = useServiceDeliveryCheck(
 *     backupCheckRequest,
 *     'backup_check_output.zip'
 *   );
 *   <BackupCheckForm onSubmit={handleSubmit} isLoading={isLoading} />
 *
 * @param {(formData: FormData) => Promise} requestFn  - axios request function
 * @param {string} outputFilename                       - default download filename
 */
export function useServiceDeliveryCheck(requestFn, outputFilename) {
  const { isLoading, request } = useLoading();
  const { showNotifications } = useNotifications();

  const handleSubmit = async (formData) => {
    try {
      const response = await request(() => requestFn(formData));

      // useLoading re-throws on network error but returns the axios error
      // object when the server responds with a non-2xx status decoded as JSON.
      if (response?.response?.data?.message) {
        showNotifications('error', response.response.data.message, 5000);
        return;
      }

      // Derive filename from Content-Disposition header when available,
      // otherwise fall back to the provided default.
      const disposition = response?.headers?.['content-disposition'] ?? '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = match?.[1] ?? outputFilename;

      FileDownload(response.data, filename);
      showNotifications('success', 'File downloaded successfully', 5000);
    } catch (error) {
      showNotifications(
        'error',
        'Something went wrong. Please try again.',
        5000
      );
    }
  };

  return { handleSubmit, isLoading };
}
